import React, { useEffect, useState } from "react";

/* -------- packages -------- */
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Container, Row } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
/* -------- ------- -------- */

/* -------- intern functions -------- */
import { clearConfigurationErrors, clearConfigurationSuccess, deleteConfigurationRequest, fetchConfigurationsRequest, fetchNotificationsRequest, fetchProductsRequest, openEditPageConfiguration, updateNotificationRequest } from "../../store/actions";
/* -------- ------- -------- */

/* -------- components -------- */
import TableContainer from "../../components/ui/List/TableContainer";
import Breadcrumb from "../../components/ui/Breadcrumbs";
import { dictionary } from "../../utils/dictionary";
import { getCurrentTheme, getErrorMessage } from "../../utils/utils";
import ErrorHandler from "../../components/ui/Error/ErrorHandler";
/* -------- ------- -------- */

const Configurators = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parameters = useSelector(state => state.Parameters);
  const navigate = useNavigate();
  document.title = `${t('configurations')} | ${parameters.appName}`; //meta title

  const user = useSelector(state => state.Login.user);

  const { configurations, errorConfig, configurationAdded } = useSelector(state => state.Configurations);
  const { notifications } = useSelector(state => state.Notifications);
  
  const projects = useSelector(state => state.Projects.projects);
  const [formattedProjects, setFormattedProjects] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingConfigToOpen, setPendingConfigToOpen] = useState(null);
  const location = useLocation();
  const projectName = location.state?.projectName;



  useEffect(() => {
    dispatch(fetchConfigurationsRequest());
    dispatch(fetchProductsRequest());
    dispatch(fetchNotificationsRequest())
  }, [dispatch]);

  useEffect(() => {
    if (projects) {
      let newProjects = projects.map((project) => {
        return {
          id: project.id,
          label: project.lib
        }
      });
      setFormattedProjects(newProjects);
    }
  }, [projects]);

/* ----------------------- Notifications --------------------------------- */
useEffect( () => {
  const notifCategory = dictionary.projQuotes.statusExpired
  const userForId = user?.id
  const groupsForId = [1, 2]
  let postedNotifs = [];
  
  configurations.forEach((config) => {
    if (config.status == notifCategory) { // Check if the configuration status is QUOTE_EXPIRED

      // Check if the notification already exists for this configuration

      if (postedNotifs.some(notification => notification === config.reference)) {
        return;
      }
  
      if (notifications && notifications.some(notification => 
        notification.cat === notifCategory && notification.lib.includes(config.reference)
      )) {
        return; // Skip if this notification already exists for this config
      }
      
      
      // Send notification to user
      const obj = {
        userForId: userForId,
        cat: notifCategory,
        lib: `Le devis de la configuration de référence [${config.reference}] a expiré.`,
        rowVersion: 0,
      }
      
      if (user.groupId !== 1 && user.groupId != 2) { // If the user is not in group 1 or 2, send notification
        dispatch(updateNotificationRequest(obj));
      }
      
      // Send notifications to groups
      for (let groupId of groupsForId) {
        const groupObj = {
          groupForId: groupId,
          cat: notifCategory,
          lib: `Le devis de la configuration de référence [${config.reference}] a expiré.`,
          rowVersion: 0,
        }
        
        dispatch(updateNotificationRequest(groupObj));
      }
      postedNotifs.push(
        config.reference
      );
    }
  }) 
}, []);
/* -------------------------------------------------------- */


const handleEditRights = (lineData) => {
  return lineData?.ProjProject?.status !== "CLOSED"
}

const handleEditConfiguration = (configId) => {
  const configObj = configurations.find(c => c.id === configId);
  if (configObj.status === "PENDING") {
    setPendingConfigToOpen(configObj);
    setShowPendingModal(true);
    return;
  } 
  dispatch(openEditPageConfiguration(configId, navigate));
};

// Configuration des colonnes
const columnConfig = [
    { key: 'id', type: 'text' },
    { key: 'lib', type: 'text' },
    { key: 'projectLib', type: 'link', link: '/projects/', linkState: { searchValue: 'projectLib' }},
    { key: 'projectStatus', type: 'tag', colorOptions: { OPENED: 'info', CLOSED: 'secondary' } },
    { key: 'reference', type: 'text' },
    { key: 'details', type: 'iconForm', icon: 'mdi mdi-folder-account-outline', libElement: 'lib', fields: 
      [
        { key: 'projectLib', type: 'text', isDisabled: true},
        { key: 'lib', type: 'text', isDisabled: true},
        { key: 'quantity', type: 'number', isDisabled: true},
        { key: 'protectionIndex', type: 'text', isDisabled: true},
        { key: 'electricalConnect', type: 'text', isDisabled: true},
        { key: 'brandLib', type: 'text', isDisabled: true},
        { key: 'powerSupply', type: 'text',  isDisabled: true},
        { key: 'powerSupplyIntensity', type: 'text', isDisabled: true},
        { key: 'nbCircuitBreakers', type: 'number', isDisabled: true},
        { key: 'nbElectricalOutlets', type: 'number', isDisabled: true},
        { key: 'safetyEquipment', type: 'text', isDisabled: true},
        { key: 'accessories', type: 'text', isDisabled: true},
      ]
    },
    { key: 'status', type: 'tag', colorOptions: { PENDING: 'info', QUOTE_OK: 'success', QUOTE_EXPIRED: 'secondary' } },
    { key: 'createdAt', type: 'date' },
    { key: 'updatedAt', type: 'date' },
  ];

  // Configuration des filtres
  const filterConfig = [
    { key: 'projectLib', type: 'select', options: formattedProjects, defaultValue: projectName },
    { key: 'status', type: 'select', options: [
      "PENDING",
      "QUOTE_OK",
      "QUOTE_EXPIRED",
    ] },
  ];

  const getRowBackgroundColor = (rowData) => {
    const currentTheme = getCurrentTheme();
    if (rowData?.ProjProject?.status == "OPENED") {
      return ''; // Fond léger rouge pour non vérifié
    }
    return (currentTheme == 'light')?'#F5F5F5':'#1a1d29'; // Aucune couleur de fond par défaut
  };

  ErrorHandler(errorConfig, clearConfigurationErrors);

  useEffect(() => {
    if (configurationAdded) {
      toast.success(t('dataSaved'));
      dispatch(clearConfigurationSuccess());
    }
  }, [configurationAdded]);

  return (

      <div className="page-content">
        <Container fluid>
          <Breadcrumb breadcrumbItem={t("configurations")}/>
          
          <TableContainer
            columnConfig={columnConfig}
            filterConfig={filterConfig}
            data={configurations}
            tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            theadClass="table-light"
            paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
            pagination="pagination"
            divClassName="table-responsive"
            isBordered={true}
            isPagination={true}
            isGlobalFilter={true}
            buttonClass="btn btn-primary btn-rounded waves-effect waves-light addContact-modal mb-2"
            buttonName="newConfiguration"
            isNewButton={false}
            prefListName="configuration"
            totalKpi={{label: 'numberOfConfigurations'}}
            isAddButton={true}
            linkToNew={'/configurations/new'}
            editFunction={handleEditConfiguration}
            deleteFunction={deleteConfigurationRequest}
            isConfig={true}
            defaultSortColumn={'lib'}
            defaultSortDirection={'asc'}
            customDeleteMessage={t('deleteConfigMessage')}
            customBulkDeleteMessage={t('deleteConfigMultipleMessage')}
            editRights={handleEditRights}
            rowBackgroundColoring={true}
            rowColoringFunction={getRowBackgroundColor}
          />
        </Container>

        {showPendingModal && (
          <Modal isOpen={showPendingModal} toggle={() => setShowPendingModal(false)}>
            <ModalHeader toggle={() => setShowPendingModal(false)}>
              {t("attention")}
            </ModalHeader>
            <ModalBody>
              {t("editWarningPending").split('\n').map((line, idx) => (
                <span key={idx}>
                  {line}
                  <br />
                </span>
              ))}
            </ModalBody>
            <ModalFooter>
              <Button color="primary"onClick={() => {
                setShowPendingModal(false);
                dispatch(openEditPageConfiguration(pendingConfigToOpen.id, navigate));
              }}>
                {t("OK")}
              </Button>
            </ModalFooter>
          </Modal>
        )}
        <ToastContainer />
      </div>

  );
};

export default Configurators;
